import { useState, useCallback, useRef } from "react";
import { useAtelierStore, JOB_STATUSES } from "@store/atelierStore";
import { WorkCharge } from "../../types/garage/atelier";
import { snapToGrid } from "@utils/timeGrid";
import { FloatingGhost } from "./FloatingGhostOverlay";
import {
  GridPreview,
  HOURS_START,
  HOURS_END,
  SNAP_PX,
  SNAP_HOUR,
  HOUR_BLOCK_HEIGHT,
} from "./gridTypes";

function getStatusConfig(status: string) {
  return JOB_STATUSES.find((s) => s.id === status)!;
}

export function useAtelierDrag(
  gridScrollRef: React.RefObject<HTMLDivElement | null>,
  sidebarRef: React.RefObject<HTMLDivElement | null>,
) {
  const updateJob = useAtelierStore((s) => s.updateJob);
  const placeChargeAsJob = useAtelierStore((s) => s.placeChargeAsJob);
  const returnJobToCharge = useAtelierStore((s) => s.returnJobToCharge);
  const moveChargeToStatus = useAtelierStore((s) => s.moveChargeToStatus);
  const getFilteredJobs = useAtelierStore((s) => s.getFilteredJobs);

  // Charge drag from sidebar
  const chargeDragRef = useRef<{
    chargeId: string;
    hours: number;
    clientName: string;
  } | null>(null);

  // Job drag from grid
  const jobDragRef = useRef<{
    jobId: string;
    mode: "move" | "resize";
    startY: number;
    origStartHour: number;
    origDuration: number;
    origWorkerId: string;
  } | null>(null);

  const [gridPreview, setGridPreview] = useState<GridPreview | null>(null);
  const [floatingGhost, setFloatingGhost] = useState<FloatingGhost | null>(
    null,
  );
  const [overDropZone, setOverDropZone] = useState(false);
  const [hoveredChargeSection, setHoveredChargeSection] = useState<
    WorkCharge["status"] | null
  >(null);

  const detectWorkerCol = useCallback(
    (clientX: number): string | null => {
      if (!gridScrollRef.current) return null;
      const cols =
        gridScrollRef.current.querySelectorAll<HTMLElement>("[data-worker-col]");
      let found: string | null = null;
      cols.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (clientX >= r.left && clientX < r.right)
          found = el.getAttribute("data-worker-col");
      });
      return found;
    },
    [gridScrollRef],
  );

  const detectDayDate = useCallback(
    (clientX: number): string | null => {
      if (!gridScrollRef.current) return null;
      const cols =
        gridScrollRef.current.querySelectorAll<HTMLElement>("[data-day-date]");
      let found: string | null = null;
      cols.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (clientX >= r.left && clientX < r.right)
          found = el.getAttribute("data-day-date");
      });
      return found;
    },
    [gridScrollRef],
  );

  const isOverGrid = useCallback(
    (clientX: number, clientY: number): boolean => {
      if (!gridScrollRef.current) return false;
      const r = gridScrollRef.current.getBoundingClientRect();
      return (
        clientX >= r.left &&
        clientX <= r.right &&
        clientY >= r.top &&
        clientY <= r.bottom
      );
    },
    [gridScrollRef],
  );

  const isOverSidebar = useCallback(
    (clientX: number, clientY: number): boolean => {
      if (!sidebarRef.current) return false;
      const r = sidebarRef.current.getBoundingClientRect();
      return (
        clientX >= r.left &&
        clientX <= r.right &&
        clientY >= r.top &&
        clientY <= r.bottom
      );
    },
    [sidebarRef],
  );

  const detectChargeSection = useCallback(
    (clientX: number, clientY: number): WorkCharge["status"] | null => {
      if (!sidebarRef.current) return null;
      const sections = sidebarRef.current.querySelectorAll<HTMLElement>(
        "[data-charge-section]",
      );
      let found: WorkCharge["status"] | null = null;
      sections.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (
          clientX >= r.left &&
          clientX <= r.right &&
          clientY >= r.top &&
          clientY <= r.bottom
        ) {
          found = el.getAttribute(
            "data-charge-section",
          ) as WorkCharge["status"];
        }
      });
      return found;
    },
    [sidebarRef],
  );

  const handleGlobalPointerMove = useCallback(
    (e: React.PointerEvent) => {
      // --- Job drag ---
      const jd = jobDragRef.current;
      if (jd) {
        if (jd.mode === "resize") {
          const deltaY = e.clientY - jd.startY;
          const snapDelta = Math.round(deltaY / SNAP_PX);
          const newDur = snapToGrid(
            jd.origDuration + snapDelta * SNAP_HOUR,
            SNAP_HOUR,
          );
          const clamped = Math.max(
            SNAP_HOUR,
            Math.min(newDur, HOURS_END - jd.origStartHour),
          );
          const dayDate = detectDayDate(e.clientX);
          setGridPreview({
            type: "job",
            jobId: jd.jobId,
            startHour: jd.origStartHour,
            durationHours: clamped,
            workerId: jd.origWorkerId,
            date: dayDate ?? undefined,
          });
          setFloatingGhost(null);
          setOverDropZone(false);
          return;
        }

        const overGrid = isOverGrid(e.clientX, e.clientY);
        const overSide = isOverSidebar(e.clientX, e.clientY);

        if (overGrid) {
          const deltaY = e.clientY - jd.startY;
          const snapDelta = Math.round(deltaY / SNAP_PX);
          const newStart = snapToGrid(
            jd.origStartHour + snapDelta * SNAP_HOUR,
            SNAP_HOUR,
          );
          const clamped = Math.max(
            HOURS_START,
            Math.min(newStart, HOURS_END - jd.origDuration),
          );
          const newWorkerId = detectWorkerCol(e.clientX) ?? jd.origWorkerId;
          const dayDate = detectDayDate(e.clientX);
          setGridPreview({
            type: "job",
            jobId: jd.jobId,
            startHour: clamped,
            durationHours: jd.origDuration,
            workerId: newWorkerId,
            date: dayDate ?? undefined,
          });
          setFloatingGhost(null);
          setOverDropZone(false);
        } else {
          const origJob = getFilteredJobs().find((j) => j.id === jd.jobId);
          const config = origJob
            ? getStatusConfig(origJob.status)
            : JOB_STATUSES[0];
          setGridPreview(null);
          setFloatingGhost({
            x: e.clientX,
            y: e.clientY,
            width: 160,
            height: 40,
            clientName: origJob?.clientName ?? "",
            hours: jd.origDuration,
            color: config.color,
            bgColor: config.bgColor,
          });
          setOverDropZone(overSide);
        }
        return;
      }

      // --- Charge drag ---
      const cd = chargeDragRef.current;
      if (cd) {
        const overGrid = isOverGrid(e.clientX, e.clientY);

        if (overGrid) {
          const workerId = detectWorkerCol(e.clientX);
          const dayDate = detectDayDate(e.clientX);
          if (workerId && gridScrollRef.current) {
            const gridRect = gridScrollRef.current.getBoundingClientRect();
            const relY =
              e.clientY - gridRect.top + gridScrollRef.current.scrollTop;
            const rawHour = HOURS_START + relY / HOUR_BLOCK_HEIGHT;
            const startHour = snapToGrid(
              Math.max(HOURS_START, Math.min(rawHour, HOURS_END - cd.hours)),
              SNAP_HOUR,
            );
            setGridPreview({
              type: "charge",
              chargeId: cd.chargeId,
              startHour,
              durationHours: cd.hours,
              workerId,
              clientName: cd.clientName,
              date: dayDate ?? undefined,
            });
          } else {
            // Over the grid area but not on a worker column (e.g. hour labels)
            // Keep last preview if any, clear ghost so it doesn't cover the grid
            setGridPreview(null);
          }
          setFloatingGhost(null);
          setHoveredChargeSection(null);
        } else {
          setGridPreview(null);
          setFloatingGhost({
            x: e.clientX,
            y: e.clientY,
            width: 160,
            height: 40,
            clientName: cd.clientName,
            hours: cd.hours,
            color: "#6366F1",
            bgColor: "#E0E6FF",
          });
          setHoveredChargeSection(detectChargeSection(e.clientX, e.clientY));
        }
        return;
      }
    },
    [
      detectWorkerCol,
      isOverGrid,
      isOverSidebar,
      getFilteredJobs,
      detectChargeSection,
      gridScrollRef,
    ],
  );

  const handleGlobalPointerUp = useCallback(() => {
    const jd = jobDragRef.current;
    if (jd) {
      if (overDropZone && jd.mode === "move") {
        returnJobToCharge(jd.jobId);
      } else if (gridPreview && gridPreview.type === "job") {
        const updates: Parameters<typeof updateJob>[1] = {
          startHour: gridPreview.startHour,
          durationHours: gridPreview.durationHours,
          workerId: gridPreview.workerId,
        };
        if (gridPreview.date) updates.date = gridPreview.date;
        updateJob(jd.jobId, updates);
      }
      jobDragRef.current = null;
      setGridPreview(null);
      setFloatingGhost(null);
      setOverDropZone(false);
      return;
    }

    const cd = chargeDragRef.current;
    if (cd) {
      if (gridPreview && gridPreview.type === "charge") {
        placeChargeAsJob(
          cd.chargeId,
          gridPreview.workerId,
          gridPreview.startHour,
          gridPreview.durationHours,
          gridPreview.date,
        );
      } else if (hoveredChargeSection) {
        moveChargeToStatus(cd.chargeId, hoveredChargeSection);
      }
      chargeDragRef.current = null;
      setGridPreview(null);
      setFloatingGhost(null);
      setHoveredChargeSection(null);
      return;
    }
  }, [
    gridPreview,
    overDropZone,
    hoveredChargeSection,
    updateJob,
    placeChargeAsJob,
    returnJobToCharge,
    moveChargeToStatus,
  ]);

  const handleChargeDragStart = useCallback(
    (e: React.PointerEvent, charge: WorkCharge) => {
      // Pointer capture is now handled by ChargeCard itself (on the card div)
      chargeDragRef.current = {
        chargeId: charge.id,
        hours: charge.hours,
        clientName: charge.clientName,
      };
      setFloatingGhost({
        x: e.clientX,
        y: e.clientY,
        width: 160,
        height: Math.max((charge.hours / SNAP_HOUR) * SNAP_PX, 40),
        clientName: charge.clientName,
        hours: charge.hours,
        color: "#6366F1",
        bgColor: "#E0E6FF",
      });
    },
    [],
  );

  const isDraggingAnything =
    !!(chargeDragRef.current || jobDragRef.current) ||
    !!(floatingGhost || gridPreview);
  const isDraggingJob =
    !!jobDragRef.current && jobDragRef.current.mode === "move";

  return {
    jobDragRef,
    gridPreview,
    setGridPreview,
    floatingGhost,
    overDropZone,
    hoveredChargeSection,
    isDraggingAnything,
    isDraggingJob,
    handleGlobalPointerMove,
    handleGlobalPointerUp,
    handleChargeDragStart,
  };
}
